import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, Trash2, X, ExternalLink, BellOff, BellRing, Share2 } from 'lucide-react';
import client from '../../../api/client';
import { subscribeToPushNotifications, isPushNotificationSupported, forceResubscribe } from '../../../utils/pushNotifications';

function isIOSSafariNotPWA() {
  const ua = window.navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/chrome|crios|fxios/i.test(ua);
  const isStandalone = window.navigator.standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;
  return isIOS && isSafari && !isStandalone;
}

const fetchNotifications = async () => {
  try {
    const response = await client.get('/notifications');
    return response.data || { notifications: [], unreadCount: 0 };
  } catch {
    return { notifications: [], unreadCount: 0 };
  }
};

const TYPE_ICON = {
  material: '📚',
  assignment: '📝',
  test: '📊',
  announcement: '📢',
  homework: '📖',
  grade: '🎯',
};

const formatTime = (dateString) => {
  const diffMs = Date.now() - new Date(dateString);
  const m = Math.floor(diffMs / 60000);
  const h = Math.floor(diffMs / 3600000);
  const d = Math.floor(diffMs / 86400000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(dateString).toLocaleDateString();
};

export function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    const data = await fetchNotifications();
    setNotifications(data.notifications || []);
    setUnreadCount(data.unreadCount || 0);
    setLoading(false);
  }, []);

  const handleMarkAsRead = async (id, e) => {
    e?.stopPropagation();
    try {
      await client.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await client.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    try {
      await client.delete(`/notifications/${id}`);
      const n = notifications.find(n => n._id === id);
      setNotifications(prev => prev.filter(n => n._id !== id));
      if (n && !n.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) handleMarkAsRead(notification._id);
    if (notification.url) window.location.href = notification.url;
    setIsOpen(false);
  };

  const handleEnablePush = async () => {
    if (!isPushNotificationSupported()) return;
    const sub = await subscribeToPushNotifications();
    if (sub) setPushPermission('granted');
    else setPushPermission(Notification.permission);
  };

  const handleTestPush = async () => {
    try {
      const res = await client.post('/notifications/test-push');
      alert(res.data?.success
        ? 'Sent! You should see a notification in ~5 seconds.'
        : 'Backend says: ' + (res.data?.message || 'unknown error'));
    } catch (err) {
      alert('Test push failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleForceResubscribe = async () => {
    const sub = await forceResubscribe();
    if (sub) {
      alert('Re-subscribed successfully. Now tap "Send test push" to verify.');
    } else {
      alert('Re-subscribe failed — check browser console for details.');
    }
  };

  // Close on outside click (desktop)
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Prevent body scroll when open on mobile
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    const handler = () => { if (document.visibilityState === 'visible') loadNotifications(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [loadNotifications]);

  const PanelContent = () => (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-slate-700" />
          <h3 className="font-sora font-bold text-[15px] text-slate-800">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors"
            >
              <Check className="w-3 h-3" /> All read
            </button>
          )}
          <button onClick={() => setIsOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      {/* iOS: needs PWA install to use push */}
      {!isPushNotificationSupported() && isIOSSafariNotPWA() && (
        <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-100 flex items-start gap-3 shrink-0">
          <Share2 className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
          <p className="text-xs text-indigo-700 font-medium leading-relaxed">
            To get push notifications on iPhone, tap <strong>Share</strong> then <strong>"Add to Home Screen"</strong> and open EduPulseAI from there.
          </p>
        </div>
      )}

      {/* Push notification permission banner */}
      {isPushNotificationSupported() && pushPermission !== 'granted' && pushPermission !== 'denied' && (
        <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-100 flex items-center gap-3 shrink-0">
          <BellRing className="w-4 h-4 text-indigo-600 shrink-0" />
          <p className="text-xs text-indigo-700 font-medium flex-1">Enable push notifications to get alerts even when the app is closed.</p>
          <button
            onClick={handleEnablePush}
            className="shrink-0 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors"
          >
            Enable
          </button>
        </div>
      )}
      {pushPermission === 'denied' && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center gap-2 shrink-0">
          <BellOff className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-700">
            {isIOSSafariNotPWA() || window.navigator.standalone
              ? 'Push blocked. Go to iPhone Settings → EduPulse → Notifications to re-enable.'
              : 'Push blocked. Enable in browser Settings → Site permissions.'}
          </p>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {loading && notifications.length === 0 ? (
          <div className="py-12 text-center">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm text-slate-400">Loading…</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-12 text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-indigo-300" />
            </div>
            <p className="font-sora font-semibold text-slate-700 text-sm">No notifications yet</p>
            <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
              You'll see alerts for tests, assignments, materials, and announcements here.
            </p>
            {isPushNotificationSupported() && pushPermission === 'default' && (
              <button
                onClick={handleEnablePush}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors"
              >
                <BellRing className="w-3.5 h-3.5" />
                Enable push alerts
              </button>
            )}
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n._id}
              onClick={() => handleNotificationClick(n)}
              className={`group flex items-start gap-3 px-4 py-3.5 border-b border-slate-100 cursor-pointer transition-colors active:bg-slate-100 ${
                !n.read ? 'bg-indigo-50/60 hover:bg-indigo-50' : 'hover:bg-slate-50'
              }`}
            >
              <span className="text-xl shrink-0 mt-0.5">{TYPE_ICON[n.type] || '🔔'}</span>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm font-semibold leading-snug ${!n.read ? 'text-slate-900' : 'text-slate-600'}`}>
                    {n.title}
                  </p>
                  {n.url && <ExternalLink className="w-3 h-3 text-slate-400 shrink-0 mt-0.5" />}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">{n.message}</p>
                <p className="text-[11px] text-slate-400 mt-1">{formatTime(n.createdAt)}</p>
              </div>

              {/* Actions — always visible on mobile, hover on desktop */}
              <div className="flex flex-col gap-1 shrink-0 sm:opacity-0 sm:group-hover:opacity-100 sm:transition-opacity">
                {!n.read && (
                  <button
                    onClick={(e) => handleMarkAsRead(n._id, e)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-indigo-100 text-indigo-500 transition-colors"
                    title="Mark as read"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={(e) => handleDelete(n._id, e)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-rose-100 text-rose-400 hover:text-rose-600 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {!n.read && (
                <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0 mt-2" />
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/80 shrink-0 flex items-center justify-between">
        {notifications.length > 0
          ? <p className="text-xs text-slate-400">{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</p>
          : <span />
        }
        {isPushNotificationSupported() && pushPermission === 'granted' && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleForceResubscribe}
              className="text-[11px] text-slate-400 hover:text-amber-500 transition-colors underline underline-offset-2"
            >
              Re-subscribe
            </button>
            <button
              onClick={handleTestPush}
              className="text-[11px] text-slate-400 hover:text-indigo-500 transition-colors underline underline-offset-2"
            >
              Test push
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={bellRef}
        onClick={() => { setIsOpen(o => !o); if (!isOpen) loadNotifications(); }}
        className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400/40"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Mobile: full-screen bottom sheet */}
            <motion.div
              className="sm:hidden fixed inset-0 z-[500] flex flex-col justify-end"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* Backdrop */}
              <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

              {/* Sheet */}
              <motion.div
                className="relative bg-white rounded-t-3xl flex flex-col overflow-hidden"
                style={{ maxHeight: '88vh' }}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 380 }}
              >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-1 shrink-0">
                  <div className="w-10 h-1 rounded-full bg-slate-200" />
                </div>
                <PanelContent />
              </motion.div>
            </motion.div>

            {/* Desktop: positioned dropdown */}
            <motion.div
              ref={dropdownRef}
              className="hidden sm:flex flex-col absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-slate-200 z-[500] overflow-hidden"
              style={{ maxHeight: '480px' }}
              initial={{ opacity: 0, scale: 0.95, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              <PanelContent />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default NotificationBell;
